
































import { Component, Vue, Prop } from 'vue-property-decorator';

import SectionData from '@/componentsV2/organisms/SectionData/SectionData.vue';
import BaseChart from '@/componentsV2/molecules/Chart/BaseChart.vue';
import { AnalyticsCardListItem } from '@/componentsV2/organisms/AnalyticsCardList/AnalyticsCardList.vue';
import { EAnalyticsCardArrowDirection } from '@/componentsV2/molecules/AnalyticsCard/AnalyticsCard.vue';

import PerformanceOverviewService from '@/api/PerformanceOverview';

import { IPeriod } from '@/models/period';
import { IChartSerie } from '@/models/performance';

import PerformanceUtils, { DatePickerShortcut } from '@/utils/PerformanceUtils';

@Component({
  components: {
    SectionData,
    BaseChart
  }
})
export default class PerformanceCoinsReports extends Vue {
  @Prop() gaEventPage!: string;

  performanceOverviewService = new PerformanceOverviewService();
  loading = false;
  period: IPeriod = PerformanceUtils.defaultPeriod();
  options = PerformanceUtils.chartOptions({
    colors: ['#73AF00', '#AF009D', '#6800BA', '#FFAE0F']
  });
  series: IChartSerie[] = [];
  analytics: Record<string, AnalyticsCardListItem> = {
    rewardedCoins: {
      hidden: true,
      insightVariant: 'normal',
      infos: {
        amount: '0',
        description: 'Rewarded Coins',
        insightText: '',
        tooltipText: '',
        counterFractionDigits: 2
      }
    },
    purchasedCoins: {
      hidden: true,
      insightVariant: 'normal',
      infos: {
        amount: '0',
        description: 'Purchased Coins',
        insightText: '',
        tooltipText: '',
        counterFractionDigits: 2
      }
    },
    freeCoins: {
      hidden: true,
      insightVariant: 'normal',
      infos: {
        amount: '0',
        description: 'Free Coins minted',
        insightText: '',
        tooltipText: '',
        counterFractionDigits: 2
      }
    },
    welcomeCoins: {
      hidden: true,
      insightVariant: 'normal',
      infos: {
        amount: '0',
        description: 'Welcome Coins minted',
        insightText: '',
        tooltipText: '',
        counterFractionDigits: 2
      }
    },
    tradedCoins: {
      hidden: true,
      insightVariant: 'normal',
      infos: {
        amount: '0',
        description: 'Traded Coins',
        insightText: '',
        tooltipText: '',
        counterFractionDigits: 2
      }
    }
  };

  analyticsMapperBySlug: Record<string, any> = {
    'rewarded-coins': {
      key: 'rewardedCoins'
    },
    'purchased-coins': {
      key: 'purchasedCoins'
    },
    'free-coins-minted': {
      key: 'welcomeCoins'
    },
    'traded-coins': {
      key: 'tradedCoins'
    }
  };

  get legendGATracking() {
    return {
      eventCategory: `${this.gaEventPage} | Coins Reports Chart - Legend`,
      eventAction: 'click'
    };
  }

  get periodGATracking() {
    return {
      period: {
        eventCategory: `${this.gaEventPage} | Coins Reports Chart - Period`,
        eventAction: 'change'
      }
    };
  }

  get translatedAnalytics() {
    return Object.entries(this.analytics)
      .map(([key, value]) => ({
        ...value,
        infos: {
          ...value.infos,
          tooltipText: this.$t(`${this.tPrefix}.analytics.${key}.tooltip`),
          description: this.$t(`${this.tPrefix}.analytics.${key}.description`)
        }
      }))
      .filter(({ hidden }) => !hidden);
  }

  get tPrefix() {
    return 'performance.overview.coinsReports';
  }

  get queryParameters() {
    return {
      from: this.period.start,
      to: this.period.end,
      ...(this.period.shortcut && {
        slug:
          PerformanceUtils.datepickerShortcutMapper[
            this.period.shortcut as DatePickerShortcut
          ]
      })
    };
  }

  created() {
    this.getCoinsReports();
  }

  async getCoinsReports({ useFilters = true } = {}) {
    this.loading = true;

    const queryParameters = useFilters ? this.queryParameters : {};

    const response = await this.performanceOverviewService.getCoinsReports(
      queryParameters
    );

    this.loading = false;

    if (response.status !== 200) return;

    for (const dataset of response.data.datasets) {
      if (!this.analyticsMapperBySlug[dataset.slug]) continue;

      const slugKey = this.analyticsMapperBySlug[dataset.slug].key;

      const datasetPercentage = Number(dataset.percentage || 0);
      const roundedDatasetPercentage = Math.round(datasetPercentage);

      this.analytics = {
        ...this.analytics,
        [slugKey]: {
          ...this.analytics[slugKey],
          insightVariant:
            roundedDatasetPercentage > 0
              ? 'success'
              : roundedDatasetPercentage < 0
              ? 'danger'
              : 'normal',
          hidden: false,
          infos: {
            ...this.analytics[slugKey].infos,
            amount: dataset.total,
            arrowDirection:
              roundedDatasetPercentage > 0
                ? EAnalyticsCardArrowDirection.UP
                : roundedDatasetPercentage < 0
                ? EAnalyticsCardArrowDirection.DOWN
                : undefined,
            insightText: this.period.shortcut
              ? `${roundedDatasetPercentage}% ${PerformanceUtils.getComparativeLabel(
                  this.period.shortcut as DatePickerShortcut
                )}`
              : ''
          }
        }
      };
    }

    this.series = PerformanceUtils.normalizedChartSeries(response.data);
  }

  handlePeriodChange(period: IPeriod) {
    this.period = PerformanceUtils.normalizedPeriod(period);
    this.getCoinsReports();
  }
}
